import { useState } from "react";

import { Button, RefreshSVG } from "@newsela/angelou";
import { useRouter } from "next/router";
import PropTypes from "prop-types";

import { withTrackEvent } from "@newsela/snitch";
import { API_BASE_URL } from "@/api/api.config";
import useRestQuery from "@/remoteState/useRestQuery";
import { isTextTooLargeToProcessForWritePrompts } from "@/utils/generatedContent";
import TT_AND_AUTO_WP_COPY from "@/view/constants/teacherToolsAndAutoGenWritePrompts";
import { AILunaTag } from "@/components/AIComponents/AILunaTag/AILunaTag";
import ReportContent from "../ReportContent";
import {
  loadingCss,
  headerCss,
  headingCss,
  disclaimerCss,
  generatedPromptCss,
  refreshButtonCss,
  promptActionsContainerCss,
} from "./AutoGeneratedWritePrompts.style";

const ButtonWithTrackEvent = withTrackEvent(Button);

const propTypes = {
  currentArticleLevel: PropTypes.object,
  onSelectPrompt: PropTypes.func,
};

const AutoGeneratedWritePrompts = ({ onSelectPrompt, currentArticleLevel }) => {
  // Hack: get story's and level's cuids out of the URL,
  // assuming a URL format of /view/<storyId>/?levelId=<levelId>

  const router = useRouter();
  const { contentId, levelId } = router.query;
  const articleIsTooLarge = isTextTooLargeToProcessForWritePrompts(
    currentArticleLevel?.text,
  );

  const { data: generatedTextAsset, loading: loadingGeneratedTextAsset } =
    useRestQuery(
      `${API_BASE_URL}/api/v3/content/${contentId}/write-prompts/?level_id=${levelId}`,
      { skip: !contentId || !levelId || articleIsTooLarge },
    );

  // Turn a markdown list into a javascript array.
  const generatedPrompts = generatedTextAsset?.algorithm_output
    .split("\n")
    .map((prompt) => prompt.replace(/^\* */, ""));

  const [shownPromptIndex, setShownPromptIndex] = useState(0);
  const shownPrompt = generatedPrompts
    ? generatedPrompts[shownPromptIndex]
    : null;
  if (articleIsTooLarge) {
    // Do not render anything when the article is too large to process.
    return null;
  }
  if (loadingGeneratedTextAsset) {
    return <div style={loadingCss}>Loading...</div>;
  }

  // Don't show anything unless we successfully fetched at least one prompt.
  if (!shownPrompt) {
    return null;
  }

  return (
    <>
      <div css={headerCss}>
        <h3 css={headingCss}>Or try this AI write prompt</h3>
        <AILunaTag />
      </div>
      <div>
        <ButtonWithTrackEvent
          flavor={Button.flavors.secondary}
          __cssFor={{ root: generatedPromptCss }}
          onClick={() => onSelectPrompt(shownPrompt)}
          dataProps={{ "data-qa-selector": "gen_write_prompt" }}
          objectType="GeneratedTextAsset"
          actionName="Select"
          streamProperties={{
            object_id: generatedTextAsset?.content_id,
            content_id: contentId,
            content_type: "GeneratedTextAsset",
            description: `Selected prompt: ${shownPrompt}`,
            component_type: "AutoGeneratedWritePrompts",
          }}
        >
          {shownPrompt}
        </ButtonWithTrackEvent>
      </div>
      <div css={promptActionsContainerCss}>
        <ButtonWithTrackEvent
          flavor={Button.flavors.muted}
          __cssFor={{ root: refreshButtonCss }}
          size={Button.sizes.md}
          icon={{
            SvgComponent: RefreshSVG,
            alignment: Button.iconAlignments.right,
          }}
          onClick={() =>
            setShownPromptIndex(
              (shownPromptIndex + 1) % generatedPrompts.length,
            )
          }
          dataProps={{ "data-qa-selector": "reject_gen_write_prompt" }}
          objectType="GeneratedTextAsset"
          actionName="Reject"
          streamProperties={{
            object_id: generatedTextAsset?.content_id,
            content_id: contentId,
            content_type: "GeneratedTextAsset",
            description: `Rejected prompt: ${shownPrompt}`,
            component_type: "AutoGeneratedWritePrompts",
          }}
        >
          Refresh
        </ButtonWithTrackEvent>
      </div>
      <div style={disclaimerCss}>{TT_AND_AUTO_WP_COPY.DISCLAIMER}</div>
      <ReportContent contentId={generatedTextAsset?.content_id} />
    </>
  );
};

AutoGeneratedWritePrompts.propTypes = propTypes;

export default AutoGeneratedWritePrompts;
